import dynamic from 'next/dynamic';
import { useState, useCallback, Dispatch, SetStateAction, MutableRefObject } from 'react';
import type { Matcher } from 'react-day-picker';
import type { InputProps, GuestRoomDetails } from '@/components/search-bar/SearchBar';
import GuestRoomContent from '@/components/search-bar/GuestRoomContent';
import { allowNumbersOnly, onPaste } from '@/utils/inputHelper';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

export type GuestRoomProps = {
  inputProps: InputProps;
  labels: Record<string, string>;
  blockedDays?: Matcher[];
  guestRoomDetails?: GuestRoomDetails;
  locale: Locale;
  toggleGuestRoomDetails?: () => void;
  setCanClosePopper?: Dispatch<SetStateAction<boolean>>;
  datePickerOpen: boolean;
  setDatePickerOpen: (value: boolean) => void;
  setUserModifiedGRDate: Dispatch<SetStateAction<boolean>>;
  triggerRef?: MutableRefObject<HTMLInputElement | null>;
};

export default function GuestRoomInput({
  inputProps,
  labels,
  blockedDays,
  guestRoomDetails,
  locale,
  setUserModifiedGRDate,
  triggerRef,
  datePickerOpen,
  setDatePickerOpen
}: GuestRoomProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const toggleGuestRoomDetails = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const dismissGuestRoomPopper = useCallback(() => {
    setIsOpen(datePickerOpen);
  }, [datePickerOpen]);

  return (
    <>
      <input
        {...inputProps}
        onClick={toggleGuestRoomDetails}
        onPaste={onPaste}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
          } else if (event.key === 'Down' || event.key === 'ArrowDown') {
            setIsOpen(true);
          } else {
            allowNumbersOnly(event);
          }
        }}
        ref={triggerRef}
      />
      {isOpen && (
        <Popper
          triggerRef={triggerRef}
          isOpen={isOpen}
          hasShadow
          onKeyDown={event => {
            if (event.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          onDismiss={dismissGuestRoomPopper}
          className="w-full rounded-lg min-[1680px]:relative min-[1680px]:left-16"
        >
          <GuestRoomContent
            triggerRef={triggerRef}
            inputProps={inputProps}
            labels={labels}
            guestRoomDetails={guestRoomDetails}
            blockedDays={blockedDays}
            locale={locale}
            toggleGuestRoomDetails={toggleGuestRoomDetails}
            datePickerOpen={datePickerOpen}
            setDatePickerOpen={setDatePickerOpen}
            setUserModifiedGRDate={setUserModifiedGRDate}
          />
        </Popper>
      )}
    </>
  );
}
